/**
 * Resources Query Hook
 *
 * The current org's Resources page. Not polled: it changes rarely, and the
 * service worker's NetworkFirst cache keeps it readable offline.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listResources, type ListResourcesResponse, type ResourceSection } from '../../api/resources';
import { queryKeys } from '../../lib/queryClient';
import { getErrorMessage } from '../../api/client';
import { useOrganization } from '../../context/OrganizationContext';

export function useResources() {
  const { currentOrg, isLoading: orgLoading } = useOrganization();
  const queryClient = useQueryClient();
  const queryKey = [...queryKeys.resources, currentOrg?.orgSlug];

  const query = useQuery({
    queryKey,
    queryFn: () => listResources(),
    enabled: !orgLoading && !!currentOrg,
  });

  return {
    sections: query.data?.sections ?? [],
    canUploadFiles: query.data?.canUploadFiles ?? false,
    loading: orgLoading || query.isLoading,
    error: query.error ? getErrorMessage(query.error) : null,
    /** Show a new order straight away, ahead of the server's answer. */
    setSections: (sections: ResourceSection[]) =>
      queryClient.setQueryData<ListResourcesResponse>(queryKey, (old) =>
        old ? { ...old, sections } : old
      ),
    invalidate: () => queryClient.invalidateQueries({ queryKey: queryKeys.resources }),
  };
}
